'use client'

import { useMemo, useState } from 'react'
import Link from 'next/link'
import ParkGlyph, { CityDots } from './ParkGlyph'
import { placeLabel, coordLabel, distanceKm } from '../../lib/cityGeo'
import { parkTitle, disambiguate } from '../../lib/parkTitle'
import { t, parkPath } from '../../lib/i18n'

/**
 * The park list: search, filters, and "near me".
 *
 * This is a client component ON PURPOSE, and it still server-renders. React renders it on the
 * server with the full, unfiltered list, so every park is in the HTML for crawlers; the browser
 * then enhances it with filtering. That is the same shape the old city page used — the mistake
 * there was not the client component, it was gating CONTENT behind `mounted`, which hid 39
 * cities of editorial from the DOM. Nothing here is gated on mount.
 *
 * FILTERS ARE OFFERED ONLY WHERE THE DATA EXISTS.
 * OpenStreetMap records almost nothing about most Spanish dog parks — in Sevilla, 20 of 25 have
 * no attribute at all. The old page showed a fixed row of filters (fenced / water / lighting /
 * agility / shade / accessible) regardless, every one of which matched zero parks, because every
 * attribute in the database was the literal string "unknown". A control that cannot return
 * anything is a lie. So each chip is built from the city's real counts and simply does not exist
 * if the count is zero.
 */

const FILTERS = [
  { key: 'fenced', label: 'Vallado', test: (p) => p.parkdata_v1?.infrastructure?.fenced === 'yes' },
  { key: 'water', label: 'Agua', test: (p) => p.parkdata_v1?.infrastructure?.water === 'yes' },
  { key: 'lighting', label: 'Iluminado', test: (p) => p.parkdata_v1?.amenities?.lighting === 'yes' },
  { key: 'accessible', label: 'Accesible', test: (p) => p.parkdata_v1?.usage?.accessibility === 'yes' },
  { key: 'agility', label: 'Agility', test: (p) => p.parkdata_v1?.infrastructure?.agility === 'yes' },
]

function fmtKm(d) {
  return d < 1 ? `${Math.round(d * 1000)} m` : `${d.toFixed(1).replace('.', ',')} km`
}

export default function ParkList({ parks, bounds, centre, citySlug, cityName , locale = 'es' }) {
  const T = t(locale)
  const [q, setQ] = useState('')
  const [active, setActive] = useState(() => new Set())
  const [here, setHere] = useState(null)      // the visitor's position, once they ask
  const [geoState, setGeoState] = useState('idle') // idle | asking | denied | unsupported

  // Only offer a filter the city can actually answer.
  const available = useMemo(
    () =>
      FILTERS.map((f) => ({ ...f, count: parks.filter(f.test).length })).filter((f) => f.count > 0),
    [parks]
  )

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    let list = parks.filter((p) => {
      for (const f of available) {
        if (active.has(f.key) && !f.test(p)) return false
      }
      if (!needle) return true
      const hay = `${p.name || ''} ${p.place_label || ''} ${placeLabel(p, centre)}`.toLowerCase()
      return hay.includes(needle)
    })

    if (here) {
      list = list
        .map((p) => ({ ...p, _d: distanceKm(here, p) }))
        .sort((a, b) => a._d - b._d)
    } else {
      // Default order: outward from the middle of town. A real order, not an arbitrary one.
      list = [...list].sort((a, b) => distanceKm(centre, a) - distanceKm(centre, b))
    }
    return disambiguate(list, locale)
  }, [parks, q, active, here, centre, available, locale])

  const toggle = (key) =>
    setActive((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })

  const locate = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGeoState('unsupported')
      return
    }
    setGeoState('asking')
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setHere({ lat: pos.coords.latitude, lng: pos.coords.longitude })
        setGeoState('idle')
      },
      () => setGeoState('denied'),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60_000 }
    )
  }

  // Madrid has 153 parks. Rendered straight down the page, the search box and the filters scrolled
  // out of view after the third result, so refining anything meant scrolling back up to find the
  // controls. The controls now stay put and the results move under them.
  //
  // A scroll box, NOT pagination: every park stays in the HTML. This is a programmatic-SEO site
  // whose whole value is that Google sees all 153 on the city page.
  const scrolls = shown.length > 6

  return (
    <>
      <div className="list-sticky">
      <div className="controls">
        <label className="field">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={T.searchIn(cityName)}
            aria-label={T.searchIn(cityName)}
          />
        </label>

        <button
          className="btn"
          onClick={locate}
          aria-pressed={Boolean(here)}
          disabled={geoState === 'asking'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3" strokeLinecap="round" />
          </svg>
          {geoState === 'asking' ? T.searching : here ? T.sortedByDistance : T.nearby}
        </button>
      </div>

      {available.length > 0 && (
        <div className="chips" role="group" aria-label="Filtrar por características">
          {available.map((f) => (
            <button
              key={f.key}
              className="chip"
              aria-pressed={active.has(f.key)}
              onClick={() => toggle(f.key)}
            >
              {f.label}
              <span className="chip-count mono">{f.count}</span>
            </button>
          ))}
        </div>
      )}

      <p className="result-note">
        {shown.length === parks.length ? T.nParks(parks.length) : T.nOfMParks(shown.length, parks.length)}
        {here && ` · ${T.sortedFromYou}`}
        {geoState === 'denied' && ` · ${T.geoDenied}`}
        {geoState === 'unsupported' && ` · ${T.geoUnavailable}`}
        {scrolls && ` · ${T.scrollList}`}
      </p>
      </div>

      {/* The town's dot cloud, ONCE. Every glyph below <use>s it instead of redrawing it.
          This is what took Madrid from 3.03 MB of HTML to ~40 KB. */}
      <CityDots id="carto-city-dots" parks={parks} bounds={bounds} />

      <ul
        className={scrolls ? 'list list--scroll' : 'list'}
        style={{ marginTop: '1.25rem' }}
        tabIndex={scrolls ? 0 : undefined}
        aria-label={scrolls ? T.nParks(shown.length) : undefined}
      >
        {shown.map((park) => {
          const tags = park.tags || []
          return (
            <li className="park" key={park.id}>
              <ParkGlyph park={park} parks={parks} bounds={bounds} dotsId="carto-city-dots" />

              <div className="park-body">
                <Link className="park-title" href={parkPath(locale, citySlug, park.park_slug)}>
                  {park._title}
                </Link>
                {/* The bearing-and-distance line is SECONDARY now. It was the only differentiator
                    when every park was called "Zona canina"; the title carries the identity. */}
                <p className="park-place">
                  {park._d != null ? (
                    <>
                      <span className="park-dist">a {fmtKm(park._d)} de ti</span>
                      {' · '}
                      {placeLabel(park, centre)}
                    </>
                  ) : (
                    placeLabel(park, centre)
                  )}
                </p>
                <p className="park-coords mono">{coordLabel(park)}</p>

                {tags.length > 0 && (
                  <div className="tags">
                    {tags.map((t) => (
                      <span key={t.label} className={t.strong ? 'tag tag-strong' : 'tag'}>
                        {t.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <span className="park-go">ver →</span>
            </li>
          )
        })}
      </ul>

      {shown.length === 0 && (
        <p className="empty">
          Ningún parque coincide con esa búsqueda en {cityName}.{' '}
          <button
            className="btn"
            style={{ marginLeft: '0.5rem' }}
            onClick={() => {
              setQ('')
              setActive(new Set())
            }}
          >
            Quitar filtros
          </button>
        </p>
      )}
    </>
  )
}
